import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Renders children at document.body level. Used for modals/overlays so
 * `position: fixed` actually anchors to the viewport — when a modal is
 * mounted inside an ancestor with `transform` (e.g. the mobile drawer
 * sliding panel) `fixed` would otherwise be relative to that ancestor
 * and collapse to 0×0.
 */
export function Portal(props: { children: React.ReactNode }) {
  // Skip the post-mount one-tick blink. We have no SSR pass — Vite serves
  // an empty <div id="root"> shell, React mounts entirely client-side, so
  // there's no hydration to mismatch. Reading `document` directly is safe
  // the first render and removes the previous "open → invisible → visible"
  // 16ms gap that gave the modal a chance to render inside its event-
  // dispatching parent before reflowing to <body>.
  const [mounted, setMounted] = useState(typeof document !== 'undefined');
  useEffect(() => {
    if (!mounted) setMounted(true);
  }, [mounted]);
  if (!mounted || typeof document === 'undefined') return null;
  return createPortal(props.children, document.body);
}
